import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ActiveEvent, Gig } from '../../../shared/types/appTypes';
import { collection, query, where, onSnapshot, getDocs, doc } from 'firebase/firestore';
import { db, functions } from 'src/firebase/firebaseConfig';
import {
  processTimestamps,
  dayliteEventTitleExtractedDate,
  dayliteEventTitleExtractedDateYYYYMMDD
} from 'src/utils/appDateFunctions';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DayliteOpportunity } from 'shared/types';

// Track active listeners to avoid duplicates
let activeGigsListener: (() => void) | null = null;
let activeEventDocListener: (() => void) | null = null;

// Initial state for the activeEvent slice
const initialState: ActiveEvent = {
  id: '',
  name: '',
  gigs: [],
  mainData: undefined,
  dayliteData: null, // Add dayliteData field
  isLoading: false,
  error: null,
  titleExtractedDate: null,
  dateISO8601: null,
  gigsLoadState: 'idle',
  gigsListenerEventId: null
};

// Helper function to update extracted dates from event name
const updateExtractedDates = (state: ActiveEvent) => {
  if (state.name) {
    state.titleExtractedDate = dayliteEventTitleExtractedDate(state.name);
    state.dateISO8601 = dayliteEventTitleExtractedDateYYYYMMDD(state.name);
  } else {
    state.titleExtractedDate = null;
    state.dateISO8601 = null;
  }
};

// Action to set just the basic info (id and name)
export const setActiveEventBasicInfo = (eventBasicInfo: { id: string; name: string }) => ({
  type: 'activeEvent/setActiveEventBasicInfo',
  payload: eventBasicInfo
});

export const fetchActiveEvent = createAsyncThunk(
  'activeEvent/fetchActiveEvent',
  async (eventId: string, { getState }) => {
    try {
      // Get the current state
      const state = getState() as { activeEvent: ActiveEvent };
      const currentEvent = state.activeEvent;

      // We already have the basic info (id and name) in state
      const eventName = currentEvent.id === eventId && currentEvent.name ? currentEvent.name : `Event ${eventId}`;

      return {
        id: eventId,
        name: eventName
      };
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }
);

// Fetch Daylite opportunity data
export const fetchDayliteOpportunity = createAsyncThunk(
  'activeEvent/fetchDayliteOpportunity',
  async (opportunityId: string) => {
    try {
      const getOpportunityFunction = httpsCallable(functions, 'getOpportunity');

      const result = await getOpportunityFunction({ id: opportunityId });
      const responseData = result.data as { data: DayliteOpportunity };
      return responseData.data;
    } catch (error) {
      console.error('Error fetching opportunity from Daylite:', error);
      throw error;
    }
  }
);

// Setup gigs listener thunk (still using Firebase for now)
//
// IMPORTANT: This thunk DOES NOT resolve when the listener is merely attached.
// It resolves only when the FIRST onSnapshot callback fires (success or error).
// This eliminates the race where `isLoading=false` flips before any data has
// arrived, causing the UI to render an empty grid for an event that actually
// has talent. See `gigsLoadState` for the user-visible lifecycle.
export const setupGigsListener = createAsyncThunk(
  'activeEvent/setupGigsListener',
  async (eventId: string, { dispatch, rejectWithValue }) => {
    // CRITICAL: Always tear down any existing listener and clear gigs first
    // so we never display stale gigs from a previous event.
    if (activeGigsListener) {
      activeGigsListener();
      activeGigsListener = null;
    }
    dispatch(gigsListenerStarted(eventId));

    return new Promise<{ eventId: string }>((resolve, reject) => {
      let firstCallback = true;
      try {
        const gigsQuery = query(collection(db, 'gigs'), where('eventId', '==', eventId));

        activeGigsListener = onSnapshot(
          gigsQuery,
          (querySnapshot) => {
            const gigs: Gig[] = [];
            querySnapshot.forEach((docSnap) => {
              const data = docSnap.data();
              const processedData = processTimestamps(data);
              gigs.push({ id: docSnap.id, ...processedData } as Gig);
            });
            dispatch(gigsSnapshotReceived({ eventId, gigs }));
            if (firstCallback) {
              firstCallback = false;
              resolve({ eventId });
            }
          },
          (error) => {
            console.error('Error fetching gigs:', error);
            dispatch(gigsSnapshotErrored({ eventId, message: error.message }));
            if (firstCallback) {
              firstCallback = false;
              // Reject so the loader knows; the snapshot error reducer
              // already populated `error` and `gigsLoadState` for the UI.
              reject(rejectWithValue(error.message));
            }
          }
        );
      } catch (error) {
        console.error('Error setting up gigs listener:', error);
        const message = error instanceof Error ? error.message : 'Failed to set up gigs listener';
        dispatch(gigsSnapshotErrored({ eventId, message }));
        reject(error);
      }
    });
  }
);

// Setup event doc listener
export const setupEventDocListener = createAsyncThunk(
  'activeEvent/setupEventDocListener',
  async (eventId: string, { dispatch }) => {
    // Clean up existing listener first
    if (activeEventDocListener) {
      activeEventDocListener();
      activeEventDocListener = null;
    }

    try {
      // Set up the listener for the single event document
      const eventDocRef = doc(db, 'events', eventId);

      activeEventDocListener = onSnapshot(
        eventDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const processedData = processTimestamps(data);
            dispatch(updateEventMainData(processedData));
          } else {
            dispatch(updateEventMainData(null));
          }
        },
        (error) => {
          console.error('Error fetching event document:', error);
          dispatch(setError(error.message));
        }
      );

      return { eventId };
    } catch (error) {
      console.error('Error setting up event document listener:', error);
      throw error;
    }
  }
);

// Clean up event doc listener
export const cleanupEventDocListener = createAsyncThunk(
  'activeEvent/cleanupEventDocListener',
  async (_, { dispatch }) => {
    if (activeEventDocListener) {
      activeEventDocListener();
      activeEventDocListener = null;
      // Use undefined (not null) to mean "not loaded". `null` is reserved
      // for the case where the doc was checked and does not exist.
      dispatch(updateEventMainData(undefined));
    }
    return null;
  }
);

// Clean up listener when no longer needed - make this more robust
export const cleanupGigsListener = createAsyncThunk('activeEvent/cleanupGigsListener', async (_, { dispatch }) => {
  if (activeGigsListener) {
    activeGigsListener();
    activeGigsListener = null;

    // Always clear gigs and reset the load lifecycle when cleaning up
    dispatch(gigsListenerCleared());
  }
  return null;
});

// Clean up all listeners when no longer needed
export const cleanupAllListeners = createAsyncThunk('activeEvent/cleanupAllListeners', async (_, { dispatch }) => {
  await dispatch(cleanupGigsListener());
  await dispatch(cleanupEventDocListener());
  return null;
});

// Create the activeEvent slice
const activeEventSlice = createSlice({
  name: 'activeEvent',
  initialState,
  reducers: {
    setActiveEvent: (state, action: PayloadAction<ActiveEvent>) => {
      const newState = { ...state, ...action.payload };
      updateExtractedDates(newState);
      return newState;
    },
    setActiveEventBasicInfo: (state, action: PayloadAction<{ id: string; name: string }>) => {
      // Reset stale event-scoped data so the next event never shows the
      // previous event's gigs/main/daylite while listeners reattach.
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.gigs = [];
      state.mainData = undefined;
      state.dayliteData = null;
      state.error = null;
      state.gigsLoadState = 'idle';
      state.gigsListenerEventId = null;
      updateExtractedDates(state);
    },
    updateGigs: (state, action: PayloadAction<Gig[]>) => {
      state.gigs = action.payload;
    },
    // Listener lifecycle reducers (used by setupGigsListener thunk).
    gigsListenerStarted: (state, action: PayloadAction<string>) => {
      state.gigs = [];
      state.gigsLoadState = 'loading';
      state.gigsListenerEventId = action.payload;
      state.error = null;
    },
    gigsSnapshotReceived: (state, action: PayloadAction<{ eventId: string; gigs: Gig[] }>) => {
      // Ignore stale snapshots from a previous listener.
      if (state.gigsListenerEventId !== action.payload.eventId) return;
      state.gigs = action.payload.gigs;
      state.gigsLoadState = 'loaded';
      state.error = null;
    },
    gigsSnapshotErrored: (state, action: PayloadAction<{ eventId: string; message: string }>) => {
      if (state.gigsListenerEventId !== action.payload.eventId) return;
      state.gigsLoadState = 'error';
      state.error = action.payload.message;
    },
    gigsListenerCleared: (state) => {
      state.gigs = [];
      state.gigsLoadState = 'idle';
      state.gigsListenerEventId = null;
    },
    updateEventMainData: (state, action: PayloadAction<any>) => {
      state.mainData = action.payload;
    },
    updateDayliteData: (state, action: PayloadAction<any>) => {
      state.dayliteData = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetActiveEvent: (state) => {
      return { ...initialState };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch active event cases
      .addCase(fetchActiveEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveEvent.fulfilled, (state, action) => {
        // Update both id and name, while preserving other state
        return {
          ...state,
          id: action.payload.id,
          name: action.payload.name, // Always update the name from the payload
          isLoading: false,
          error: null
        };
      })
      .addCase(fetchActiveEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch event';
      })
      // Setup gigs listener cases. NOTE: `gigsLoadState` is the source of
      // truth for whether gigs have been received from Firestore. We keep
      // `isLoading` in sync for backward compatibility with existing
      // consumers (e.g. DataGrid loading prop), but the UI must gate
      // talent-list rendering on `gigsLoadState === 'loaded'`.
      .addCase(setupGigsListener.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setupGigsListener.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(setupGigsListener.rejected, (state, action) => {
        state.isLoading = false;
        // gigsSnapshotErrored already set state.error/gigsLoadState; only
        // populate error here as a fallback for thunk-level rejections
        // (e.g., setup threw before the snapshot error path ran).
        if (!state.error) {
          state.error = action.error.message || 'Failed to setup gigs listener';
        }
        if (state.gigsLoadState !== 'error') {
          state.gigsLoadState = 'error';
        }
      })
      // Setup event doc listener cases
      .addCase(setupEventDocListener.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setupEventDocListener.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(setupEventDocListener.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to setup event document listener';
      })
      // Fetch Daylite opportunity data cases
      .addCase(fetchDayliteOpportunity.pending, (state) => {
        // We don't set isLoading here since this is non-blocking
      })
      .addCase(fetchDayliteOpportunity.fulfilled, (state, action) => {
        state.dayliteData = action.payload as DayliteOpportunity;
      })
      .addCase(fetchDayliteOpportunity.rejected, (state, action) => {
        console.error('Failed to fetch Daylite data:', action.error);
        // We don't set the main error state since this is non-blocking
      });
  }
});

export const {
  setActiveEvent,
  setActiveEventBasicInfo: setBasicInfoAction,
  updateGigs,
  updateEventMainData,
  updateDayliteData,
  setError,
  resetActiveEvent,
  gigsListenerStarted,
  gigsSnapshotReceived,
  gigsSnapshotErrored,
  gigsListenerCleared
} = activeEventSlice.actions;

// Selectors
export const selectEventExtractedDate = (state: { activeEvent: ActiveEvent }) => state.activeEvent.titleExtractedDate;
export const selectEventExtractedDateYYYYMMDD = (state: { activeEvent: ActiveEvent }) => {
  if (state.activeEvent.mainData !== undefined) {
    return state.activeEvent.mainData?.dateISO8601 || null;
  }

  return state.activeEvent.dateISO8601;
};

export default activeEventSlice.reducer;
